import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { hoyNayarit } from "@/lib/fechaLocal";
import { toast } from "sonner";
import { CheckCircle2, ListChecks, ChevronRight, RefreshCw, Plus, AlertTriangle, Clock, Radio, Circle } from "lucide-react";

const CATEGORIES = [
  { code: "procesos", label: "Procesos" },
  { code: "limpieza", label: "Limpieza" },
  { code: "supervision", label: "Supervisión" },
];

const STATUS_LABEL = {
  pending: "Pendiente", in_progress: "En progreso", completed: "Completado",
  completed_with_observations: "Con observaciones", abandoned: "Abandonado", expired: "Expirado",
};
const STATUS_COLOR = {
  pending: "bg-slate-100 text-slate-600",
  in_progress: "bg-yellow-100 text-yellow-700",
  completed: "bg-green-100 text-green-800",
  completed_with_observations: "bg-orange-100 text-orange-700",
  abandoned: "bg-red-100 text-red-700",
  expired: "bg-slate-200 text-slate-600",
};
const TURN_LABEL = { apertura: "Apertura", intermedio: "Producción", cierre: "Cierre" };
const REFRESH_MS = 25000;

export default function OpsChecklists() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState(searchParams.get("category") || "procesos");
  const [date, setDate] = useState(() => hoyNayarit());
  const [branchFilter, setBranchFilter] = useState("");
  const [branches, setBranches] = useState([]);
  const [runs, setRuns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startingTemplate, setStartingTemplate] = useState("");
  const [liveRows, setLiveRows] = useState(null);
  const liveTimerRef = useRef(null);
  const refreshTimerRef = useRef(null);

  const isAdmin = ["corporate_admin", "treasury"].includes(user?.role);
  const isControlTower = ["corporate_admin", "direccion"].includes(user?.role);

  const loadBranches = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get("/branches");
      setBranches(res.data.filter((b) => !b.is_corporate));
    } catch {}
  }, [isAdmin]);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await api.get(`/ops/checklist-templates?category_code=${activeCategory}&active=true`);
      setTemplates(res.data);
    } catch {
      setTemplates([]);
    }
  }, [activeCategory]);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams({ date, category_code: activeCategory });
      if (branchFilter) params.set("branch_id", branchFilter);
      const res = await api.get(`/ops/checklists?${params}`);
      setRuns(res.data);
    } catch {
      if (!silent) setRuns([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [date, activeCategory, branchFilter]);

  const loadLive = useCallback(async () => {
    if (!isControlTower) return;
    try {
      const res = await api.get(`/ops/checklists/live?date=${date}`);
      setLiveRows(res.data.branches || []);
    } catch {
      setLiveRows(null);
    }
  }, [isControlTower, date]);

  useEffect(() => { loadBranches(); }, [loadBranches]);
  useEffect(() => { loadTemplates(); load(); loadLive(); }, [loadTemplates, load, loadLive]);

  // Fix WH36 (2026-09-08): `activeCategory` sólo leía ?category= al montar. Con la
  // entrada nueva del menú ("Protocolos de limpieza" -> ?category=limpieza), quien
  // ya estaba parado en Checklists cambiaba la URL y seguía viendo Procesos: la
  // pantalla no se vuelve a montar. Ahora la pestaña sigue al parámetro.
  useEffect(() => {
    const c = searchParams.get("category");
    if (c && c !== activeCategory) setActiveCategory(c);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh de la vista de monitoreo (torre de control + lista de runs)
  useEffect(() => {
    refreshTimerRef.current = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(refreshTimerRef.current);
  }, [load]);

  useEffect(() => {
    if (!isControlTower) return;
    liveTimerRef.current = setInterval(loadLive, REFRESH_MS);
    return () => clearInterval(liveTimerRef.current);
  }, [isControlTower, loadLive]);

  const availableToStart = useMemo(() => {
    const startedCodes = new Set(runs.map((r) => r.template_code));
    return templates.filter((t) => !startedCodes.has(t.code));
  }, [templates, runs]);

  const handleStart = async (templateId) => {
    const branchId = branchFilter || user?.branch_id;
    if (!branchId) { toast.error("Selecciona una sucursal"); return; }
    try {
      const res = await api.post("/ops/checklists/start", { template_id: templateId, branch_id: branchId });
      toast.success("Checklist iniciado");
      navigate(`/ops/checklists/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo iniciar el checklist");
    } finally {
      setStartingTemplate("");
    }
  };


  return (
    <div className="p-6 space-y-5" data-testid="ops-checklists-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Checklists</h1>
          <p className="text-sm text-slate-500">Plantillas operativas con evidencia fotográfica</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
            data-testid="checklists-date-filter"
          />
          {isAdmin && (
            <select
              value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
              data-testid="checklists-branch-filter"
            >
              <option value="">Todas las sucursales</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <button onClick={() => load()} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors" data-testid="checklists-refresh-btn">
            <RefreshCw className="w-4 h-4 text-slate-500" />
          </button>
        </div>
      </div>

      {isControlTower && liveRows && (
        <div className="bg-slate-900 rounded-xl p-4 space-y-3" data-testid="control-tower-panel">
          <div className="flex items-center gap-2 text-white">
            <Radio className="w-4 h-4 text-emerald-400 animate-pulse" />
            <span className="font-semibold text-sm">Torre de control · en vivo</span>
            <span className="text-xs text-slate-400">se actualiza sola cada {REFRESH_MS / 1000}s</span>
          </div>
          {liveRows.length === 0 ? (
            <p className="text-sm text-slate-400">Sin checklists generados para esta fecha.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="control-tower-branches">
              {liveRows.map((b) => (
                <div key={b.branch_id} className="bg-slate-800 rounded-lg p-3 space-y-1.5" data-testid={`control-tower-branch-${b.branch_id}`}>
                  <div className="text-xs font-semibold text-white truncate">{b.branch_name}</div>
                  <div className="w-full bg-slate-700 rounded-full h-1.5">
                    <div className="bg-[#FFDF00] h-1.5 rounded-full transition-all" style={{ width: `${b.avg_percentage}%` }} />
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-[10px]">
                    {b.pending > 0 && <span className="px-1.5 py-0.5 rounded-full bg-slate-600 text-slate-200">{b.pending} pend.</span>}
                    {b.in_progress > 0 && <span className="px-1.5 py-0.5 rounded-full bg-yellow-600/80 text-white">{b.in_progress} en curso</span>}
                    {b.completed > 0 && <span className="px-1.5 py-0.5 rounded-full bg-green-600/80 text-white">{b.completed} ok</span>}
                    {b.completed_with_observations > 0 && <span className="px-1.5 py-0.5 rounded-full bg-orange-600/80 text-white">{b.completed_with_observations} obs.</span>}
                    {b.abandoned > 0 && <span className="px-1.5 py-0.5 rounded-full bg-red-600/80 text-white">{b.abandoned} abnd.</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tabs por categoría */}
      <div className="flex gap-1 border-b border-slate-200" data-testid="checklists-category-tabs">
        {CATEGORIES.map((c) => (
          <button
            key={c.code}
            onClick={() => { setActiveCategory(c.code); setSearchParams({ category: c.code }); }}
            data-testid={`checklists-tab-${c.code}`}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px ${
              activeCategory === c.code ? "border-[#FFDF00] text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Iniciar plantilla manualmente */}
      {availableToStart.length > 0 && (branchFilter || user?.branch_id) && (
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={startingTemplate}
            onChange={(e) => setStartingTemplate(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
            data-testid="checklists-start-template-select"
          >
            <option value="">Iniciar plantilla...</option>
            {availableToStart.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button
            disabled={!startingTemplate}
            onClick={() => handleStart(startingTemplate)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#FFDF00] text-sm font-semibold text-slate-900 hover:bg-yellow-300 transition-colors disabled:opacity-40"
            data-testid="checklists-start-btn"
          >
            <Plus className="w-4 h-4" /> Iniciar
          </button>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="bg-white rounded-xl border border-slate-200 h-20 animate-pulse" />)}</div>
      ) : runs.length === 0 ? (
        <div className="text-center py-16 space-y-2" data-testid="checklists-empty-state">
          <ListChecks className="w-12 h-12 text-slate-300 mx-auto" />
          <p className="text-slate-500">No hay checklists de {CATEGORIES.find((c) => c.code === activeCategory)?.label.toLowerCase()} para esta fecha</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="checklists-run-list">
          {runs.map((r) => {
            const pct = r.summary?.percentage_complete ?? 0;
            return (
              <button
                key={r.id}
                onClick={() => navigate(`/ops/checklists/${r.id}`)}
                className="w-full flex items-center gap-4 bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-all text-left"
                data-testid={`checklist-run-${r.id}`}
              >
                <div className="w-10 h-10 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center flex-shrink-0">
                  {r.status === "completed" ? <CheckCircle2 className="w-5 h-5 text-green-600" />
                    : r.status === "completed_with_observations" ? <AlertTriangle className="w-5 h-5 text-orange-500" />
                    : r.status === "abandoned" ? <AlertTriangle className="w-5 h-5 text-red-500" />
                    : r.status === "pending" ? <Circle className="w-5 h-5 text-slate-300" />
                    : <Clock className="w-5 h-5 text-slate-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{r.template_name}</div>
                  <div className="text-xs text-slate-500">
                    {r.branch_name} {r.turn ? `· ${TURN_LABEL[r.turn] || r.turn}` : ""}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 max-w-[140px] bg-slate-100 rounded-full h-1.5">
                      <div className="bg-[#FFDF00] h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs text-slate-500">{pct.toFixed(0)}%</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR[r.status]}`}>
                    {STATUS_LABEL[r.status] || r.status}
                  </span>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
